import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import './Grafik.css';
import rotationsAPI from '../../api/rotations';

function Grafik({ projects = [], users = [], currentUser, contracts = {} }) {
  const [selectedProject, setSelectedProject] = useState(projects.length > 0 ? projects[0] : '');
  const [selectedRotation, setSelectedRotation] = useState('');
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [scheduleData, setScheduleData] = useState({});
  const [rotations, setRotations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [isSummaryExpanded, setIsSummaryExpanded] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [selectedContractType, setSelectedContractType] = useState('');
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [infoMessage, setInfoMessage] = useState('');
  const [showAbsenceModal, setShowAbsenceModal] = useState(false);
  const [absenceModalData, setAbsenceModalData] = useState(null);
  const [modalMode, setModalMode] = useState(null); // 'overtime' lub 'absence'
  const [showClearHoursModal, setShowClearHoursModal] = useState(false);

  // Nazwy miesięcy po polsku
  const monthNames = [
    'Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec',
    'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień'
  ];

  // Nazwy dni tygodnia po polsku
  const dayNames = ['Pon', 'Wt', 'Śr', 'Czw', 'Pt', 'So', 'Nie'];

  // Typy dni wolnych/absencyjnych zgodnych z polskim prawem pracy
  const absenceTypes = {
    'urlop': { label: 'Urlop', color: '#3b82f6', icon: '🏖️' },
    'urlop_na_zadanie': { label: 'Urlop na żądanie', color: '#60a5fa', icon: '🏝️' },
    'chorobowe': { label: 'Zwolnienie lekarskie', color: '#f59e0b', icon: '🏥' },
    'opieka': { label: 'Opieka nad dzieckiem', color: '#ec4899', icon: '👶' },
    'okolicznosciowy': { label: 'Urlop okolicznościowy', color: '#8b5cf6', icon: '🎉' },
    'bezplatny': { label: 'Urlop bezpłatny', color: '#6b7280', icon: '📅' },
    'swieto': { label: 'Święto', color: '#ef4444', icon: '🎊' }
  };

  // Wczytaj rotacje dla wybranego projektu z API
  useEffect(() => {
    const loadRotations = async () => {
      if (!selectedProject) {
        setRotations([]);
        setSelectedRotation('');
        return;
      }

      try {
        const rotationsData = await rotationsAPI.getByProject(selectedProject);
        const formattedRotations = rotationsData.map(r => ({
          id: r.id.toString(),
          title: r.title,
          rotationNumber: r.rotationNumber,
          pattern: r.pattern || [],
          daysPerCycle: r.daysPerCycle || 9,
          project: r.project || selectedProject
        }));
        setRotations(formattedRotations);
      } catch (error) {
        console.error('Błąd ładowania rotacji:', error);
        setRotations([]);
      }
    };

    loadRotations();
    
    // Nasłuchuj aktualizacji rotacji
    const handleRotationsUpdate = () => {
      loadRotations();
    };
    window.addEventListener('rotationsUpdated', handleRotationsUpdate);
    
    return () => {
      window.removeEventListener('rotationsUpdated', handleRotationsUpdate);
    };
  }, [selectedProject]);

  // Filtruj pracowników przypisanych do wybranej rotacji i projektu
  useEffect(() => {
    if (!selectedProject || !selectedRotation || !users) {
      setEmployees([]);
      return;
    }

    // Jeśli wybrano "Wszystkie zmiany", pokaż wszystkich pracowników z projektu
    if (selectedRotation === 'all') {
      const filteredEmployees = users.filter(user => {
        // Sprawdź czy to pracownik (employee)
        if (user.role !== 'employee') return false;
        
        // Sprawdź czy jest przypisany do wybranego projektu
        const hasProject = user.projects && user.projects.includes(selectedProject);
        return hasProject;
      });

      setEmployees(filteredEmployees);
      return;
    }

    // Znajdź rotację po ID
    const rotation = rotations.find(r => String(r.id) === String(selectedRotation));
    if (!rotation) {
      setEmployees([]);
      return;
    }

    // Filtruj pracowników (employee) przypisanych do tego projektu i rotacji
    const filteredEmployees = users.filter(user => {
      // Sprawdź czy to pracownik (employee)
      if (user.role !== 'employee') return false;
      
      // Sprawdź czy jest przypisany do wybranego projektu
      const hasProject = user.projects && user.projects.includes(selectedProject);
      if (!hasProject) return false;

      // Sprawdź czy ma przypisaną tę samą rotację
      const userRotationId = user.rotationId ? String(user.rotationId) : null;
      const userShift = user.shift || '';
      
      // Porównaj po ID rotacji lub tytule
      const matchesRotation = userRotationId === String(selectedRotation) || 
                              userShift === rotation.title;
      
      return matchesRotation;
    });

    setEmployees(filteredEmployees);
  }, [selectedProject, selectedRotation, users, rotations]);

  // Wczytaj dane grafiku
  useEffect(() => {
    if (!selectedProject || !selectedRotation) {
      setScheduleData({});
      return;
    }

    // Jeśli wybrano "Wszystkie zmiany", połącz dane ze wszystkich rotacji
    if (selectedRotation === 'all') {
      const allScheduleData = {};
      
      // Przejdź przez wszystkie rotacje dla danego projektu
      rotations.forEach(rotation => {
        const key = `schedule_${selectedProject}_${rotation.id}_${currentYear}_${currentMonth}`;
        const savedData = localStorage.getItem(key);
        if (savedData) {
          const rotationData = JSON.parse(savedData);
          // Połącz dane z każdej rotacji (nadpisz jeśli są konflikty - ostatnia rotacja wygrywa)
          Object.keys(rotationData).forEach(dateKey => {
            if (!allScheduleData[dateKey]) {
              allScheduleData[dateKey] = {};
            }
            Object.assign(allScheduleData[dateKey], rotationData[dateKey]);
          });
        }
      });
      
      setScheduleData(allScheduleData);
      return;
    }

    const key = `schedule_${selectedProject}_${selectedRotation}_${currentYear}_${currentMonth}`;
    const savedData = localStorage.getItem(key);
    if (savedData) {
      setScheduleData(JSON.parse(savedData));
    } else {
      setScheduleData({});
    }
  }, [selectedProject, selectedRotation, currentMonth, currentYear, rotations]);

  // Zapisz dane grafiku
  const saveScheduleData = (data) => {
    if (!selectedProject || !selectedRotation) return;
    
    // Jeśli wybrano "Wszystkie zmiany", zapisz dane dla każdego pracownika do jego rotacji
    if (selectedRotation === 'all') {
      // Dla każdego pracownika znajdź jego rotację i zapisz dane do odpowiedniego klucza
      employees.forEach(employee => {
        const employeeId = employee.email || employee.id;
        const employeeRotationId = employee.rotationId ? String(employee.rotationId) : null;
        
        if (employeeRotationId) {
          // Filtruj dane tylko dla tego pracownika
          const employeeData = {};
          Object.keys(data).forEach(dateKey => {
            if (data[dateKey] && data[dateKey][employeeId]) {
              if (!employeeData[dateKey]) {
                employeeData[dateKey] = {};
              }
              employeeData[dateKey][employeeId] = data[dateKey][employeeId];
            }
          });
          
          // Wczytaj istniejące dane dla tej rotacji
          const key = `schedule_${selectedProject}_${employeeRotationId}_${currentYear}_${currentMonth}`;
          const existingData = localStorage.getItem(key);
          let rotationData = existingData ? JSON.parse(existingData) : {};
          
          // Zaktualizuj dane dla tego pracownika
          Object.keys(employeeData).forEach(dateKey => {
            if (!rotationData[dateKey]) {
              rotationData[dateKey] = {};
            }
            rotationData[dateKey][employeeId] = employeeData[dateKey][employeeId];
          });
          
          // Zapisz zaktualizowane dane
          localStorage.setItem(key, JSON.stringify(rotationData));
        }
      });
      
      setScheduleData(data);
      return;
    }
    
    const key = `schedule_${selectedProject}_${selectedRotation}_${currentYear}_${currentMonth}`;
    localStorage.setItem(key, JSON.stringify(data));
    setScheduleData(data);
  };

  // Pobierz liczbę dni w miesiącu
  const getDaysInMonth = (month, year) => {
    return new Date(year, month + 1, 0).getDate();
  };

  // Formatuj datę jako klucz (YYYY-MM-DD)
  const formatDateKey = (day) => {
    const date = new Date(currentYear, currentMonth, day);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const dayStr = String(day).padStart(2, '0');
    return `${year}-${month}-${dayStr}`;
  };

  // Sprawdź czy dzień jest weekendem
  const isWeekend = (day) => {
    const date = new Date(currentYear, currentMonth, day);
    const dayOfWeek = date.getDay();
    return dayOfWeek === 0 || dayOfWeek === 6;
  };

  // Oblicz datę Wielkanocy (algorytm Meeusa/Jonesa/Butchera)
  const calculateEaster = (year) => {
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    const day = ((h + l - 7 * m + 114) % 31) + 1;
    return new Date(year, month - 1, day);
  };

  // Oblicz wszystkie polskie święta dla danego roku (wolne od pracy)
  const getPolishHolidays = (year) => {
    const holidays = [];
    
    // Święta stałe (wolne od pracy)
    holidays.push(new Date(year, 0, 1));   // Nowy Rok
    holidays.push(new Date(year, 0, 6));   // Trzech Króli
    holidays.push(new Date(year, 4, 1));   // Święto Pracy
    holidays.push(new Date(year, 4, 3));   // Święto Konstytucji 3 Maja
    holidays.push(new Date(year, 7, 15));  // Wniebowzięcie NMP
    holidays.push(new Date(year, 10, 1));  // Wszystkich Świętych
    holidays.push(new Date(year, 10, 11)); // Święto Niepodległości
    
    // Wigilia (24 grudnia) - dzień wolny od 2025 roku
    if (year >= 2025) {
      holidays.push(new Date(year, 11, 24)); // Wigilia
    }
    
    holidays.push(new Date(year, 11, 25)); // Boże Narodzenie (pierwszy dzień)
    holidays.push(new Date(year, 11, 26)); // Boże Narodzenie (drugi dzień)
    
    // Święta ruchome (zależne od Wielkanocy)
    const easter = calculateEaster(year);
    
    // Poniedziałek Wielkanocny (dzień po Wielkanocy)
    const easterMonday = new Date(easter);
    easterMonday.setDate(easterMonday.getDate() + 1);
    holidays.push(easterMonday);
    
    // Boże Ciało (60 dni po Wielkanocy, czwartek)
    const corpusChristi = new Date(easter);
    corpusChristi.setDate(corpusChristi.getDate() + 60);
    holidays.push(corpusChristi);
    
    // Sprawdź, które święta przypadają w sobotę i dodaj dodatkowy dzień wolny
    // (zgodnie z polskim prawem pracy, za święto w sobotę przysługuje dodatkowy dzień wolny)
    // UWAGA: Nie dodawaj dodatkowego dnia wolnego, jeśli następny poniedziałek to 3 listopada
    // (3 listopada nie jest świętem i dniem wolnym od pracy)
    const saturdayHolidays = holidays.filter(holiday => {
      const dayOfWeek = holiday.getDay();
      return dayOfWeek === 6; // Sobota
    });
    
    // Dodaj dodatkowe dni wolne za święta w soboty (następny poniedziałek lub inny dzień)
    saturdayHolidays.forEach(holiday => {
      // Dodaj poniedziałek po święcie w sobotę jako dodatkowy dzień wolny
      const additionalDay = new Date(holiday);
      additionalDay.setDate(additionalDay.getDate() + 2); // Poniedziałek
      
      // Sprawdź, czy to nie jest 3 listopada (nie jest świętem)
      const isNovember3 = additionalDay.getMonth() === 10 && additionalDay.getDate() === 3;
      if (isNovember3) {
        return; // Nie dodawaj 3 listopada jako dnia wolnego
      }
      
      // Sprawdź, czy ten dzień nie jest już świętem
      const isAlreadyHoliday = holidays.some(h => 
        h.getFullYear() === additionalDay.getFullYear() &&
        h.getMonth() === additionalDay.getMonth() &&
        h.getDate() === additionalDay.getDate()
      );
      if (!isAlreadyHoliday) {
        holidays.push(additionalDay);
      }
    });
    
    // Usuń 3 listopada z listy świąt (nie jest świętem i dniem wolnym od pracy)
    const filteredHolidays = holidays.filter(holiday => {
      const isNovember3 = holiday.getMonth() === 10 && holiday.getDate() === 3;
      return !isNovember3;
    });
    
    return filteredHolidays;
  };

  // Sprawdź czy dzień jest polskim świętem
  const isHoliday = (day) => {
    const date = new Date(currentYear, currentMonth, day);
    const holidays = getPolishHolidays(currentYear);
    
    return holidays.some(holiday => {
      return holiday.getFullYear() === date.getFullYear() &&
             holiday.getMonth() === date.getMonth() &&
             holiday.getDate() === date.getDate();
    });
  };

  // Obsługa zmiany miesiąca
  const handleMonthChange = (direction) => {
    if (direction === 'prev') {
      if (currentMonth === 0) {
        setCurrentMonth(11);
        setCurrentYear(currentYear - 1);
      } else {
        setCurrentMonth(currentMonth - 1);
      }
    } else {
      if (currentMonth === 11) {
        setCurrentMonth(0);
        setCurrentYear(currentYear + 1);
      } else {
        setCurrentMonth(currentMonth + 1);
      }
    }
  };

  // Obsługa zmiany roku
  const handleYearChange = (direction) => {
    if (direction === 'prev') {
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentYear(currentYear + 1);
    }
  };

  const handleClearAllHours = () => {
    if (!selectedProject || !selectedRotation) return;
    
    const daysInMonth = getDaysInMonth(currentMonth, currentYear);
    const newData = { ...scheduleData };
    let hasChanges = false;
    
    employees.forEach(employee => {
      const employeeId = employee.email || employee.id;
      
      for (let day = 1; day <= daysInMonth; day++) {
        const dateKey = formatDateKey(day);
        
        if (newData[dateKey] && newData[dateKey][employeeId]) {
          const employeeDayData = newData[dateKey][employeeId];
          
          // Wyczyść wszystkie godziny, ale zachowaj absencje
          if (employeeDayData.shiftDay || employeeDayData.shiftAfternoon || 
              employeeDayData.shiftNight || employeeDayData.overtimeDay || 
              employeeDayData.overtimeNight) {
            newData[dateKey][employeeId] = {
              ...employeeDayData,
              shiftDay: 0,
              shiftAfternoon: 0,
              shiftNight: 0,
              overtimeDay: 0,
              overtimeNight: 0
            };
            hasChanges = true;
          }
        }
      }
    });
    
    if (hasChanges) {
      const key = `schedule_${selectedProject}_${selectedRotation}_${currentYear}_${currentMonth}`;
      localStorage.setItem(key, JSON.stringify(newData));
      setScheduleData(newData);
    }
    
    setShowClearHoursModal(false);
  };

  // Nowa struktura danych dla 5 typów zmian (bez popołudniowych nadgodzin)
  const getDefaultShiftData = () => ({
    shiftDay: 0,           // Z.Dzienna (6:00-14:00)
    shiftAfternoon: 0,     // Z.Popołudniowa (14:00-22:00)
    shiftNight: 0,         // Z.Nocna (22:00-6:00)
    overtimeDay: 0,        // Nad.Dzienne (6:00-14:00)
    overtimeNight: 0,      // Nad.Nocne (22:00-6:00)
    absenceTypeDay: '',     // Typ absencji dla Z.Dzienna
    absenceTypeAfternoon: '', // Typ absencji dla Z.Popołudniowa
    absenceTypeNight: ''    // Typ absencji dla Z.Nocna
  });

  // Obsługa zmiany wartości dla konkretnego typu zmiany
  const handleShiftChange = (employeeId, day, shiftType, value) => {
    const dateKey = formatDateKey(day);
    const hours = parseFloat(value) || 0;
    
    // Znajdź pracownika i sprawdź rodzaj zatrudnienia
    const employee = employees.find(emp => {
      const empId = emp.email || emp.id;
      return empId === employeeId;
    });
    const employmentType = employee?.employmentType || '';
    const isContractWork = employmentType === 'Umowa zlecenie'; // Umowa zlecenie - wszystkie godziny jako zwykłe
    
    const newData = { ...scheduleData };
    if (!newData[dateKey]) {
      newData[dateKey] = {};
    }
    if (!newData[dateKey][employeeId]) {
      newData[dateKey][employeeId] = getDefaultShiftData();
    }
    
    // Sprawdź czy wprowadzono więcej niż 8 godzin (tylko dla typów zmian, nie dla nadgodzin)
    const isShiftType = shiftType === 'shiftDay' || shiftType === 'shiftAfternoon' || shiftType === 'shiftNight';
    
    // Dla "Umowa zlecenie" nie otwieramy modala nadgodzin - wszystkie godziny jako zwykłe
    if (isShiftType && hours > 8 && !isContractWork) {
      // Określ typ nadgodzin na podstawie typu zmiany
      let overtimeType = 'overtimeDay';
      if (shiftType === 'shiftNight') {
        overtimeType = 'overtimeNight';
      } else if (shiftType === 'shiftDay' || shiftType === 'shiftAfternoon') {
        overtimeType = 'overtimeDay';
      }
      
      // Oblicz liczbę nadgodzin (godziny powyżej 8)
      const overtimeHours = hours - 8;
      
      // Zostaw wpisaną wartość w komórce (np. 12)
      newData[dateKey][employeeId][shiftType] = hours;
      
      // Zapisz nadgodziny
      newData[dateKey][employeeId][overtimeType] = overtimeHours;
      
      // Pobierz aktualne dane pracownika dla modala
      const employeeDayData = newData[dateKey][employeeId];
      
      // Otwórz modal z nadgodzinami (opcjonalnie, użytkownik może edytować)
      setAbsenceModalData({
        employeeId,
        day,
        shiftType: shiftType, // Dodaj shiftType, aby wiedzieć, którą zmianę edytujemy
        overtimeType,
        overtimeHours: overtimeHours.toString(),
        currentOvertimeDay: employeeDayData.overtimeDay || 0,
        currentOvertimeNight: employeeDayData.overtimeNight || 0
      });
      setModalMode('overtime');
      setShowAbsenceModal(true);
    } else if (isShiftType) {
      // Jeśli zmieniono liczbę godzin dla typu zmiany
      const currentHours = newData[dateKey][employeeId][shiftType] || 0;
      
      // Dla "Umowa zlecenie" wszystkie godziny jako zwykłe (bez nadgodzin)
      if (isContractWork) {
        // Po prostu zapisz wszystkie godziny jako zwykłe
        newData[dateKey][employeeId][shiftType] = hours;
        // Wyczyść nadgodziny (jeśli były)
        if (shiftType === 'shiftDay' || shiftType === 'shiftAfternoon') {
          newData[dateKey][employeeId].overtimeDay = 0;
        } else if (shiftType === 'shiftNight') {
          newData[dateKey][employeeId].overtimeNight = 0;
        }
      } else if (hours > 8) {
        // Określ typ nadgodzin
        let overtimeType = 'overtimeDay';
        if (shiftType === 'shiftNight') {
          overtimeType = 'overtimeNight';
        } else if (shiftType === 'shiftDay' || shiftType === 'shiftAfternoon') {
          overtimeType = 'overtimeDay';
        }
        
        // Oblicz nowe nadgodziny
        const overtimeHours = hours - 8;
        newData[dateKey][employeeId][shiftType] = hours;
        newData[dateKey][employeeId][overtimeType] = overtimeHours;
      } else {
        // Jeśli zmniejszono do 8 lub mniej, usuń nadgodziny
        newData[dateKey][employeeId][shiftType] = hours;
        if (shiftType === 'shiftDay' || shiftType === 'shiftAfternoon') {
          newData[dateKey][employeeId].overtimeDay = 0;
        } else if (shiftType === 'shiftNight') {
          newData[dateKey][employeeId].overtimeNight = 0;
        }
      }
    } else {
      // Jeśli to nadgodziny, zapisz normalnie
      newData[dateKey][employeeId][shiftType] = hours;
    }
    
    // Jeśli wprowadzono godziny, usuń typ absencji dla tego typu zmiany
    if (hours > 0) {
      if (shiftType === 'shiftDay') {
        newData[dateKey][employeeId].absenceTypeDay = '';
      } else if (shiftType === 'shiftAfternoon') {
        newData[dateKey][employeeId].absenceTypeAfternoon = '';
      } else if (shiftType === 'shiftNight') {
        newData[dateKey][employeeId].absenceTypeNight = '';
      }
    }
    
    saveScheduleData(newData);
  };

  // Obsługa zmiany godzin dziennych dla pracownika w danym dniu (kompatybilność wsteczna)
  const handleDayHoursChange = (employeeId, day, value) => {
    handleShiftChange(employeeId, day, 'shiftDay', value);
  };

  // Obsługa zmiany godzin nocnych dla pracownika w danym dniu (kompatybilność wsteczna)
  const handleNightHoursChange = (employeeId, day, value) => {
    handleShiftChange(employeeId, day, 'shiftNight', value);
  };

  // Funkcja do czyszczenia nadgodzin dla danego dnia i pracownika
  const handleClearOvertime = (employeeId, day, overtimeType) => {
    const dateKey = formatDateKey(day);
    // Utwórz głęboką kopię danych
    const newData = JSON.parse(JSON.stringify(scheduleData));
    
    if (!newData[dateKey] || !newData[dateKey][employeeId]) {
      return; // Brak danych do wyczyszczenia
    }
    
    const employeeDayData = newData[dateKey][employeeId];
    
    // Wyczyść wszystkie nadgodziny dla danego dnia (zarówno dzienne jak i nocne)
    newData[dateKey][employeeId] = {
      ...employeeDayData,
      overtimeDay: 0,
      overtimeNight: 0
    };
    
    // Ustaw shiftDay/shiftAfternoon na maksymalnie 8 godzin (jeśli były > 8)
    if ((employeeDayData.shiftDay || 0) > 8) {
      newData[dateKey][employeeId].shiftDay = 8;
    }
    if ((employeeDayData.shiftAfternoon || 0) > 8) {
      newData[dateKey][employeeId].shiftAfternoon = 8;
    }
    
    // Ustaw shiftNight na maksymalnie 8 godzin (jeśli było > 8)
    if ((employeeDayData.shiftNight || 0) > 8) {
      newData[dateKey][employeeId].shiftNight = 8;
    }
    
    // Zapisz dane i zaktualizuj stan
    saveScheduleData(newData);
    
    // Zamknij modal i wyczyść dane
    setShowAbsenceModal(false);
    setAbsenceModalData(null);
    setModalMode(null);
  };

  // Obsługa zmiany typu absencji dla konkretnej zmiany
  const handleAbsenceChange = (employeeId, day, shiftType, absenceType) => {
    const dateKey = formatDateKey(day);
    
    const newData = { ...scheduleData };
    if (!newData[dateKey]) {
      newData[dateKey] = {};
    }
    if (!newData[dateKey][employeeId]) {
      newData[dateKey][employeeId] = getDefaultShiftData();
    }
    
    // Ustaw absencję dla konkretnego typu zmiany
    if (shiftType === 'shiftDay') {
      newData[dateKey][employeeId].absenceTypeDay = absenceType;
      // Jeśli wybrano absencję, ustaw godziny dla tej zmiany na 0
    if (absenceType) {
        newData[dateKey][employeeId].shiftDay = 0;
      }
    } else if (shiftType === 'shiftAfternoon') {
      newData[dateKey][employeeId].absenceTypeAfternoon = absenceType;
      if (absenceType) {
        newData[dateKey][employeeId].shiftAfternoon = 0;
      }
    } else if (shiftType === 'shiftNight') {
      newData[dateKey][employeeId].absenceTypeNight = absenceType;
      if (absenceType) {
        newData[dateKey][employeeId].shiftNight = 0;
      }
    }
    
    saveScheduleData(newData);
  };

  // Mapowanie typów absencji na oznaczenia ze zrzutów
  const absenceTypeLabels = {
    'swieto': 'ŚWIĘTO',
    'chorobowe': 'L4',
    'bezplatny': 'O',
    'urlop_na_zadanie': 'U',
    'urlop': 'U',
    'okolicznosciowy': 'U',
    'opieka': 'U'
  };

  // Automatycznie oznacz święta dla wszystkich pracowników przy pierwszym wczytaniu
  useEffect(() => {
    if (!selectedProject || !selectedRotation || employees.length === 0) return;
    
    // Jeśli wybrano "Wszystkie zmiany", pomiń automatyczne oznaczanie świąt
    // (każdy pracownik ma swoją rotację, więc nie możemy użyć jednej rotacji)
    if (selectedRotation === 'all') return;
    
    const holidays = getPolishHolidays(currentYear);
    let hasChanges = false;
    const newData = { ...scheduleData };
    
    // Najpierw usuń oznaczenie "swieto" dla 3 listopada (nie jest świętem)
    if (currentMonth === 10) { // Listopad
      const day = 3;
      const dateKey = formatDateKey(day);
      
      if (newData[dateKey]) {
        employees.forEach(employee => {
          const employeeId = employee.email || employee.id;
          if (newData[dateKey][employeeId]) {
            const shiftData = newData[dateKey][employeeId];
            // Usuń oznaczenie święta dla 3 listopada
            if (shiftData.absenceTypeDay === 'swieto') {
              newData[dateKey][employeeId].absenceTypeDay = '';
              hasChanges = true;
            }
            if (shiftData.absenceTypeAfternoon === 'swieto') {
              newData[dateKey][employeeId].absenceTypeAfternoon = '';
              hasChanges = true;
            }
            if (shiftData.absenceTypeNight === 'swieto') {
              newData[dateKey][employeeId].absenceTypeNight = '';
              hasChanges = true;
            }
            // Kompatybilność wsteczna
            if (shiftData.absenceType === 'swieto') {
              delete newData[dateKey][employeeId].absenceType;
              hasChanges = true;
            }
          }
        });
      }
    }
    
    holidays.forEach(holiday => {
      if (holiday.getMonth() === currentMonth && holiday.getFullYear() === currentYear) {
        const day = holiday.getDate();
        
        // Pomiń 3 listopada (nie jest świętem)
        if (day === 3 && holiday.getMonth() === 10) {
          return;
        }
        
        const dateKey = formatDateKey(day);
        
        if (!newData[dateKey]) {
          newData[dateKey] = {};
        }
        
        employees.forEach(employee => {
          const employeeId = employee.email || employee.id;
          if (!newData[dateKey][employeeId]) {
            newData[dateKey][employeeId] = getDefaultShiftData();
          }
          
          // Jeśli nie ma jeszcze oznaczonego święta i nie ma wprowadzonych godzin, automatycznie oznacz jako święto
          const shiftData = newData[dateKey][employeeId];
          const hasAnyHours = (shiftData.shiftDay || 0) + (shiftData.shiftAfternoon || 0) + 
                             (shiftData.shiftNight || 0) + (shiftData.overtimeDay || 0) + 
                             (shiftData.overtimeNight || 0) > 0;
          
          // Sprawdź czy jest jakakolwiek absencja
          const hasAnyAbsence = shiftData.absenceTypeDay || shiftData.absenceTypeAfternoon || shiftData.absenceTypeNight;
          
          // Kompatybilność wsteczna: sprawdź stary format
          const hasOldAbsence = shiftData.absenceType && !hasAnyAbsence;
          
          if (!hasAnyAbsence && !hasOldAbsence && !hasAnyHours) {
            // Sprawdź, na jaką zmianę pracownik jest przypisany w rotacji (jeśli jest rotacja)
            // W trybie "Wszystkie zmiany" użyj rotacji pracownika
            const employeeRotationId = employee.rotationId ? String(employee.rotationId) : null;
            const shiftInfo = getShiftForDay(day, employeeRotationId);
            if (shiftInfo && !shiftInfo.isOff) {
              // Określ typ zmiany na podstawie nazwy zmiany w rotacji
              const shiftName = (shiftInfo.shiftName || '').toLowerCase();
              if (shiftName.includes('dzien') || shiftName.includes('day') || shiftName.includes('dzienna')) {
                newData[dateKey][employeeId].absenceTypeDay = 'swieto';
              } else if (shiftName.includes('popołudni') || shiftName.includes('afternoon') || shiftName.includes('popołudniowa')) {
                newData[dateKey][employeeId].absenceTypeAfternoon = 'swieto';
              } else if (shiftName.includes('noc') || shiftName.includes('night') || shiftName.includes('nocna')) {
                newData[dateKey][employeeId].absenceTypeNight = 'swieto';
              } else {
                // Domyślnie przypisz do zmiany dziennej
                newData[dateKey][employeeId].absenceTypeDay = 'swieto';
              }
            } else {
              // Jeśli nie ma rotacji, przypisz domyślnie do zmiany dziennej
              newData[dateKey][employeeId].absenceTypeDay = 'swieto';
            }
            hasChanges = true;
          }
        });
      }
    });
    
    if (hasChanges) {
      const key = `schedule_${selectedProject}_${selectedRotation}_${currentYear}_${currentMonth}`;
      localStorage.setItem(key, JSON.stringify(newData));
      setScheduleData(newData);
    }
  }, [selectedProject, selectedRotation, currentMonth, currentYear, employees]);

  // Oblicz wszystkie statystyki dla sekcji PODSUMOWANIE
  const calculateSummaryStatistics = (employeeId) => {
    const daysInMonth = getDaysInMonth(currentMonth, currentYear);
    
    // Znajdź pracownika i sprawdź rodzaj zatrudnienia
    const employee = employees.find(emp => {
      const empId = emp.email || emp.id;
      return empId === employeeId;
    });
    const employmentType = employee?.employmentType || '';
    const isContractWork = employmentType === 'Umowa zlecenie'; // Umowa zlecenie - wszystkie godziny jako zwykłe
    
    const stats = {
      totalHours: 0,
      totalDayShift: 0,
      totalAfternoonShift: 0,
      totalNightShift: 0,
      overtimeDay: 0,
      overtimeNight: 0,
      saturdays: 0,
      sundays: 0,
      vacation: 0,
      sickLeave: 0,
      delegation: 0,
      careLeave: 0,
      occasionalLeave: 0,
      unpaidAbsence: 0,
      leaveOnDemand: 0,
      pekkasDay: 0,
      forceMajeure: 0,
      workingDays: 0
    };
    
    for (let day = 1; day <= daysInMonth; day++) {
      const dateKey = formatDateKey(day);
      const date = new Date(currentYear, currentMonth, day);
      const dayOfWeek = date.getDay();
      const isSaturday = dayOfWeek === 6;
      const isSunday = dayOfWeek === 0;
      
      const dayData = scheduleData[dateKey];
      if (dayData && dayData[employeeId]) {
        const employeeDayData = dayData[employeeId];
        
        // Kompatybilność wsteczna: konwersja starego formatu
        let shiftData = { ...getDefaultShiftData() };
        if (employeeDayData.hours !== undefined && employeeDayData.shiftDay === undefined) {
          // Stary format - konwertuj
          const oldAbsenceType = employeeDayData.absenceType || '';
          shiftData.shiftDay = employeeDayData.hours || 0;
          shiftData.shiftNight = employeeDayData.nightHours || 0;
          // Jeśli była absencja w starym formacie, przypisz ją do zmiany dziennej (domyślnie)
          shiftData.absenceTypeDay = oldAbsenceType;
        } else {
          shiftData = { ...getDefaultShiftData(), ...employeeDayData };
          // Kompatybilność wsteczna: jeśli jest stary format z absenceType, skonwertuj
          if (shiftData.absenceType && !shiftData.absenceTypeDay && !shiftData.absenceTypeAfternoon && !shiftData.absenceTypeNight) {
            shiftData.absenceTypeDay = shiftData.absenceType;
            delete shiftData.absenceType;
          }
        }
        
        // Sprawdź czy są jakiekolwiek godziny (zmiany lub nadgodziny)
        const hasAnyHours = (shiftData.shiftDay || 0) + (shiftData.shiftAfternoon || 0) + 
                            (shiftData.shiftNight || 0) + (shiftData.overtimeDay || 0) + 
                            (shiftData.overtimeNight || 0) > 0;
        
        // Jeśli nie ma żadnych godzin, pomiń ten dzień
        if (!hasAnyHours) {
          continue;
        }
        
        // WAŻNE: shiftDay/shiftNight zawierają pełną wartość (np. 12), nie tylko 8
        // Nadgodziny są zapisane osobno jako overtimeDay/overtimeNight
        // Więc shiftDay = 12 oznacza: 8 godzin normalnych + 4 nadgodziny
        // Ale w danych mamy: shiftDay = 12, overtimeDay = 4 (nadgodziny są zapisane osobno)
        
        // Nadgodziny są zapisane osobno
        const overtimeDayHours = shiftData.overtimeDay || 0;
        const overtimeNightHours = shiftData.overtimeNight || 0;
        
        // Sprawdź czy są explicit nadgodziny (zapisanę osobno jako overtimeDay/overtimeNight)
        // Jeśli są explicit nadgodziny, używamy ich
        // Jeśli nie ma explicit nadgodzin, ale shiftDay/shiftNight > 8, to obliczamy z wartości shiftDay/shiftNight
        const hasExplicitOvertimeDay = overtimeDayHours > 0;
        const hasExplicitOvertimeNight = overtimeNightHours > 0;
        const hasOvertimeInShiftDay = (shiftData.shiftDay || 0) > 8;
        const hasOvertimeInShiftNight = (shiftData.shiftNight || 0) > 8;
        
        // Oblicz rzeczywiste godziny normalne
        // Jeśli są explicit nadgodziny, shiftDay/shiftNight zawierają tylko normalne godziny (max 8)
        // Jeśli nie ma explicit nadgodzin, shiftDay/shiftNight mogą zawierać nadgodziny
        let normalDayHours = 0;
        let normalNightHours = 0;
        
        if (hasExplicitOvertimeDay || hasExplicitOvertimeNight) {
          // Jeśli są explicit nadgodziny, shiftDay/shiftNight zawierają tylko normalne godziny
          normalDayHours = Math.min(shiftData.shiftDay || 0, 8);
          normalNightHours = Math.min(shiftData.shiftNight || 0, 8);
        } else {
          // Jeśli nie ma explicit nadgodzin, shiftDay/shiftNight mogą zawierać nadgodziny
          normalDayHours = Math.min(shiftData.shiftDay || 0, 8);
          normalNightHours = Math.min(shiftData.shiftNight || 0, 8);
        }
        
        const normalAfternoonHours = shiftData.shiftAfternoon || 0; // Popołudniowa nie ma nadgodzin
        
        // Rzeczywiste nadgodziny:
        // - Jeśli są explicit nadgodziny (overtimeDay/overtimeNight > 0), używamy ich
        // - Jeśli nie ma explicit nadgodzin, ale shiftDay > 8, to nadgodziny = shiftDay - 8
        // UWAGA: Dla "Umowa zlecenie" wszystkie godziny traktujemy jako zwykłe (brak nadgodzin)
        let actualOvertimeDay = 0;
        let actualOvertimeNight = 0;
        
        if (!isContractWork) {
          // Dla "Umowa o pracę" - normalne naliczanie nadgodzin
          // Priorytet: najpierw sprawdź explicit nadgodziny
          // Jeśli są jakiekolwiek explicit nadgodziny, nie liczymy nadgodzin z shiftDay/shiftNight
          if (hasExplicitOvertimeDay) {
            actualOvertimeDay = overtimeDayHours;
          } else if (hasExplicitOvertimeNight) {
            // Jeśli są explicit nadgodziny nocne, ale nie dzienne, to shiftDay nie zawiera nadgodzin dziennych
            actualOvertimeDay = 0;
          } else if (hasOvertimeInShiftDay) {
            // Tylko jeśli nie ma żadnych explicit nadgodzin, liczymy z shiftDay
            actualOvertimeDay = (shiftData.shiftDay || 0) - 8;
          }
          
          if (hasExplicitOvertimeNight) {
            actualOvertimeNight = overtimeNightHours;
          } else if (hasExplicitOvertimeDay) {
            // Jeśli są explicit nadgodziny dzienne, ale nie nocne, to shiftNight nie zawiera nadgodzin nocnych
            actualOvertimeNight = 0;
          } else if (hasOvertimeInShiftNight) {
            // Tylko jeśli nie ma żadnych explicit nadgodzin, liczymy z shiftNight
            actualOvertimeNight = (shiftData.shiftNight || 0) - 8;
          }
        } else {
          // Dla "Umowa zlecenie" - wszystkie godziny jako zwykłe, brak nadgodzin
          actualOvertimeDay = 0;
          actualOvertimeNight = 0;
        }
        
        // Dla "Umowa zlecenie" - wszystkie godziny traktujemy jako zwykłe
        // Więc jeśli shiftDay > 8, to wszystkie godziny idą do normalnych
        // Dla "Umowa o pracę" - jeśli są explicit nadgodziny, nie liczymy nadgodzin z shiftDay/shiftNight
        let totalDayHours = 0;
        let totalNightHours = 0;
        
        if (isContractWork) {
          totalDayHours = shiftData.shiftDay || 0;
          totalNightHours = shiftData.shiftNight || 0;
        } else {
          // Dla "Umowa o pracę"
          // Jeśli są explicit nadgodziny, używamy ich i nie liczymy nadgodzin z shiftDay/shiftNight
          if (hasExplicitOvertimeDay || hasExplicitOvertimeNight) {
            // Jeśli są explicit nadgodziny, shiftDay/shiftNight zawierają tylko normalne godziny (max 8)
            totalDayHours = normalDayHours + actualOvertimeDay;
            totalNightHours = normalNightHours + actualOvertimeNight;
          } else {
            // Jeśli nie ma explicit nadgodzin, shiftDay/shiftNight mogą zawierać nadgodziny
            totalDayHours = normalDayHours + actualOvertimeDay;
            totalNightHours = normalNightHours + actualOvertimeNight;
          }
        }
        
        const totalAfternoon = normalAfternoonHours;
        
        // Suma wszystkich godzin
        stats.totalHours += totalDayHours + totalAfternoon + totalNightHours;
        
        // Sumy zmian
        // Dla "Umowa zlecenie" - wszystkie godziny jako zwykłe
        if (isContractWork) {
          stats.totalDayShift += shiftData.shiftDay || 0;
          stats.totalAfternoonShift += normalAfternoonHours;
          stats.totalNightShift += shiftData.shiftNight || 0;
        } else {
          // Dla "Umowa o pracę" - normalne godziny (max 8) + nadgodziny osobno
          stats.totalDayShift += normalDayHours;
          stats.totalAfternoonShift += normalAfternoonHours;
          stats.totalNightShift += normalNightHours;
        }
        
        // Nadgodziny (tylko dla "Umowa o pracę" i tylko jeśli są rzeczywiście wprowadzone)
        if (!isContractWork) {
          if (actualOvertimeDay > 0) {
            stats.overtimeDay += actualOvertimeDay;
          }
          if (actualOvertimeNight > 0) {
            stats.overtimeNight += actualOvertimeNight;
          }
        }
        
        // Soboty i niedziele
        const totalHoursForDay = totalDayHours + totalAfternoon + totalNightHours;
        if (isSaturday && totalHoursForDay > 0) {
          stats.saturdays += totalHoursForDay;
        }
        if (isSunday && totalHoursForDay > 0) {
          stats.sundays += totalHoursForDay;
        }
        
        // Dni robocze - sprawdź czy jest jakakolwiek absencja
        const hasAnyAbsence = shiftData.absenceTypeDay || shiftData.absenceTypeAfternoon || shiftData.absenceTypeNight;
        if (!isSaturday && !isSunday && !hasAnyAbsence && totalHoursForDay > 0) {
          stats.workingDays++;
        }
        
        // Typy absencji - sprawdź wszystkie typy zmian
        // UWAGA: 'swieto' nie jest liczone w podsumowaniu, zgodnie z wymaganiami
        const processAbsenceType = (absenceType) => {
          if (!absenceType) return;
          // Pomijaj święta w podsumowaniu
          if (absenceType === 'swieto') return;
          
          switch (absenceType) {
            case 'urlop':
              stats.vacation++;
              break;
            case 'chorobowe':
              stats.sickLeave++;
              break;
            case 'delegacja':
              stats.delegation++;
              break;
            case 'opieka':
              stats.careLeave++;
              break;
            case 'okolicznosciowy':
              stats.occasionalLeave++;
              break;
            case 'bezplatny':
              stats.unpaidAbsence++;
              break;
            case 'urlop_na_zadanie':
              stats.leaveOnDemand++;
              break;
            default:
              break;
          }
        };
        
        // Przetwórz absencje dla każdego typu zmiany
        processAbsenceType(shiftData.absenceTypeDay);
        processAbsenceType(shiftData.absenceTypeAfternoon);
        processAbsenceType(shiftData.absenceTypeNight);
      }
    }
    
    return stats;
  };

  // Oblicz sumę godzin dla pracownika w miesiącu (kompatybilność wsteczna)
  const calculateMonthlyHours = (employeeId) => {
    const stats = calculateSummaryStatistics(employeeId);
    return {
      total: stats.totalHours,
      day: stats.totalDayShift + stats.totalAfternoonShift,
      night: stats.totalNightShift
    };
  };

  // Oblicz nadgodziny (powyżej 8h dziennie lub 40h tygodniowo)
  const calculateOvertime = (employeeId) => {
    // Znajdź pracownika i sprawdź rodzaj zatrudnienia
    const employee = employees.find(emp => {
      const empId = emp.email || emp.id;
      return empId === employeeId;
    });
    const employmentType = employee?.employmentType || '';
    const isContractWork = employmentType === 'Umowa zlecenie'; // Umowa zlecenie - brak nadgodzin
    
    // Jeśli "Umowa zlecenie", zwróć zero nadgodzin
    if (isContractWork) {
      return {
        dailyOvertimeDay: 0,
        dailyOvertimeNight: 0,
        weeklyOvertimeDay: 0,
        weeklyOvertimeNight: 0
      };
    }
    
    const daysInMonth = getDaysInMonth(currentMonth, currentYear);
    let dailyOvertimeDay = 0;
    let dailyOvertimeNight = 0;
    let weeklyOvertimeDay = 0;
    let weeklyOvertimeNight = 0;
    
    // Zbierz dane dla każdego dnia
    const dailyData = [];
    for (let day = 1; day <= daysInMonth; day++) {
      const dateKey = formatDateKey(day);
      const dayData = scheduleData[dateKey];
      if (dayData && dayData[employeeId]) {
        const employeeDayData = dayData[employeeId];
        // Kompatybilność wsteczna: jeśli istnieje stary format z 'hours', traktuj jako godziny dzienne
        let dayHours = 0;
        let nightHours = 0;
        if (employeeDayData.hours !== undefined && employeeDayData.dayHours === undefined) {
          dayHours = employeeDayData.hours || 0;
        } else {
          dayHours = employeeDayData.dayHours || 0;
          nightHours = employeeDayData.nightHours || 0;
        }
        const totalHours = dayHours + nightHours;
        
        dailyData.push({
          day,
          dayHours,
          nightHours,
          totalHours
        });
        
        // Nadgodziny dzienne (powyżej 8h dziennie)
        if (totalHours > 8) {
          const overtime = totalHours - 8;
          // Proporcjonalnie rozdziel nadgodziny na dzienne i nocne
          if (totalHours > 0) {
            const dayRatio = dayHours / totalHours;
            const nightRatio = nightHours / totalHours;
            dailyOvertimeDay += overtime * dayRatio;
            dailyOvertimeNight += overtime * nightRatio;
          }
        }
      } else {
        dailyData.push({
          day,
          dayHours: 0,
          nightHours: 0,
          totalHours: 0
        });
      }
    }
    
    // Nadgodziny tygodniowe (powyżej 40h w tygodniu)
    const weeks = Math.ceil(daysInMonth / 7);
    for (let week = 0; week < weeks; week++) {
      let weekDayHours = 0;
      let weekNightHours = 0;
      let weekTotalHours = 0;
      
      for (let day = week * 7 + 1; day <= Math.min((week + 1) * 7, daysInMonth); day++) {
        const dayData = dailyData[day - 1];
        if (dayData) {
          weekDayHours += dayData.dayHours;
          weekNightHours += dayData.nightHours;
          weekTotalHours += dayData.totalHours;
        }
      }
      
      if (weekTotalHours > 40) {
        const weeklyOvertime = weekTotalHours - 40;
        // Proporcjonalnie rozdziel nadgodziny tygodniowe na dzienne i nocne
        if (weekTotalHours > 0) {
          const dayRatio = weekDayHours / weekTotalHours;
          const nightRatio = weekNightHours / weekTotalHours;
          weeklyOvertimeDay += weeklyOvertime * dayRatio;
          weeklyOvertimeNight += weeklyOvertime * nightRatio;
        }
      }
    }
    
    // Nadgodziny płatne 100% = maksimum z nadgodzin dziennych (suma) lub tygodniowych (suma)
    const totalDailyOvertime = dailyOvertimeDay + dailyOvertimeNight;
    const totalWeeklyOvertime = weeklyOvertimeDay + weeklyOvertimeNight;
    const overtime100 = Math.max(totalDailyOvertime, totalWeeklyOvertime);
    
    // Nadgodziny dzienne i nocne to maksimum z nadgodzin dziennych lub tygodniowych
    const finalOvertimeDay = Math.max(dailyOvertimeDay, weeklyOvertimeDay);
    const finalOvertimeNight = Math.max(dailyOvertimeNight, weeklyOvertimeNight);
    
    return { 
      dailyDay: finalOvertimeDay, 
      dailyNight: finalOvertimeNight,
      daily: finalOvertimeDay + finalOvertimeNight,
      weekly: totalWeeklyOvertime, 
      total: Math.max(totalDailyOvertime, totalWeeklyOvertime),
      overtime100: overtime100
    };
  };

  // Oblicz sumę dni wolnych dla każdego typu absencji
  const calculateAbsenceSummary = (employeeId) => {
    const daysInMonth = getDaysInMonth(currentMonth, currentYear);
    const summary = {};
    
    Object.keys(absenceTypes).forEach(type => {
      summary[type] = 0;
    });
    
    for (let day = 1; day <= daysInMonth; day++) {
      const dateKey = formatDateKey(day);
      const dayData = scheduleData[dateKey];
      if (dayData && dayData[employeeId]) {
        const employeeDayData = dayData[employeeId];
        
        // Kompatybilność wsteczna: konwersja starego formatu
        let absenceTypeDay = employeeDayData.absenceTypeDay || '';
        let absenceTypeAfternoon = employeeDayData.absenceTypeAfternoon || '';
        let absenceTypeNight = employeeDayData.absenceTypeNight || '';
        
        // Jeśli jest stary format z absenceType, skonwertuj
        if (employeeDayData.absenceType && !absenceTypeDay && !absenceTypeAfternoon && !absenceTypeNight) {
          absenceTypeDay = employeeDayData.absenceType;
        }
        
        // Zlicz absencje dla każdego typu zmiany
        [absenceTypeDay, absenceTypeAfternoon, absenceTypeNight].forEach(absenceType => {
          if (absenceType && summary[absenceType] !== undefined) {
          summary[absenceType]++;
        }
        });
      }
    }
    
    return summary;
  };

  // Oblicz numer tygodnia w roku dla danego dnia (ISO 8601 - tydzień zaczyna się w poniedziałek)
  // Używa niezawodnej metody, która poprawnie obsługuje luty i lata przestępne
  const getWeekOfYear = (date) => {
    // Utwórz kopię daty, aby nie modyfikować oryginału
    const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const originalYear = d.getFullYear();
    const originalMonth = d.getMonth();
    const originalDate = d.getDate();
    
    // ISO 8601: Tydzień 1 to tydzień zawierający pierwszy czwartek roku
    // Przesuń datę do czwartku tego tygodnia (ISO 8601 używa czwartku jako punktu odniesienia)
    const dayOfWeek = d.getDay() || 7; // 0 = Niedziela -> 7, 1-6 = Pon-Sob
    const daysToThursday = 4 - dayOfWeek; // Ile dni do czwartku (może być ujemne)
    const thursday = new Date(d);
    thursday.setDate(d.getDate() + daysToThursday);
    
    // Sprawdź, czy czwartek jest w tym samym roku co oryginalna data
    // Jeśli oryginalna data jest w grudniu (miesiąc 11), a czwartek jest w styczniu następnego roku,
    // to data należy do ostatniego tygodnia poprzedniego roku
    if (originalMonth === 11 && thursday.getMonth() === 0 && thursday.getFullYear() === originalYear + 1) {
      // Data jest w grudniu, ale czwartek jest w styczniu następnego roku
      // Oblicz numer tygodnia dla poprzedniego roku
      const prevYear = originalYear;
      const prevJan1 = new Date(prevYear, 0, 1);
      const prevJan1Day = prevJan1.getDay() || 7;
      
      let prevDaysToFirstThursday = 0;
      if (prevJan1Day <= 4) {
        prevDaysToFirstThursday = 4 - prevJan1Day;
      } else {
        prevDaysToFirstThursday = 11 - prevJan1Day;
      }
      const prevFirstThursday = new Date(prevYear, 0, 1 + prevDaysToFirstThursday);
      
      // Znajdź ostatni czwartek grudnia poprzedniego roku
      // Przesuń 31 grudnia do czwartku tego tygodnia (wstecz, jeśli trzeba)
      const prevDec31 = new Date(prevYear, 11, 31);
      const prevDec31Day = prevDec31.getDay() || 7;
      // Oblicz ile dni wstecz do czwartku (czwartek = 4)
      let daysBackToThursday = 0;
      if (prevDec31Day === 4) {
        daysBackToThursday = 0; // 31 grudnia to czwartek
      } else if (prevDec31Day < 4) {
        // 31 grudnia jest przed czwartkiem (pon-śr), więc czwartek jest w przyszłości
        // Ale to nie może być, więc czwartek musi być w poprzednim tygodniu
        daysBackToThursday = prevDec31Day + 3; // np. pon (1) -> czw poprzedniego tygodnia = 1 + 3 = 4 dni wstecz
      } else {
        // 31 grudnia jest po czwartku (pt-nie), więc czwartek jest wcześniej
        daysBackToThursday = prevDec31Day - 4; // np. pt (5) -> czw = 5 - 4 = 1 dzień wstecz
      }
      const prevLastThursday = new Date(prevYear, 11, 31 - daysBackToThursday);
      
      // Oblicz numer tygodnia dla ostatniego czwartku grudnia
      const msPerDay = 1000 * 60 * 60 * 24;
      const prevDaysDiff = Math.round((prevLastThursday.getTime() - prevFirstThursday.getTime()) / msPerDay);
      const prevWeekNum = Math.floor(prevDaysDiff / 7) + 1;
      
      return prevWeekNum;
    }
    
    // Znajdź pierwszy czwartek roku
    const year = thursday.getFullYear();
    const jan1 = new Date(year, 0, 1);
    const jan1Day = jan1.getDay() || 7;
    
    // Oblicz datę pierwszego czwartku roku
    let daysToFirstThursday = 0;
    if (jan1Day <= 4) {
      daysToFirstThursday = 4 - jan1Day;
    } else {
      daysToFirstThursday = 11 - jan1Day;
    }
    const firstThursday = new Date(year, 0, 1 + daysToFirstThursday);
    
    // Oblicz różnicę w milisekundach i przekonwertuj na dni
    const msPerDay = 1000 * 60 * 60 * 24;
    const daysDiff = Math.round((thursday.getTime() - firstThursday.getTime()) / msPerDay);
    
    // Oblicz numer tygodnia
    const weekNum = Math.floor(daysDiff / 7) + 1;
    
    // Jeśli tydzień jest <= 0, należy do poprzedniego roku
    if (weekNum <= 0) {
      const prevYear = year - 1;
      const prevJan1 = new Date(prevYear, 0, 1);
      const prevJan1Day = prevJan1.getDay() || 7;
      
      let prevDaysToFirstThursday = 0;
      if (prevJan1Day <= 4) {
        prevDaysToFirstThursday = 4 - prevJan1Day;
      } else {
        prevDaysToFirstThursday = 11 - prevJan1Day;
      }
      const prevFirstThursday = new Date(prevYear, 0, 1 + prevDaysToFirstThursday);
      
      const prevDaysDiff = Math.round((thursday.getTime() - prevFirstThursday.getTime()) / msPerDay);
      const prevWeekNum = Math.floor(prevDaysDiff / 7) + 1;
      
      // Sprawdź ile tygodni było w poprzednim roku
      const prevDec31 = new Date(prevYear, 11, 31);
      const prevDec31Day = prevDec31.getDay() || 7;
      let prevDaysToLastThursday = 0;
      if (prevDec31Day <= 4) {
        prevDaysToLastThursday = 4 - prevDec31Day;
      } else {
        prevDaysToLastThursday = 11 - prevDec31Day;
      }
      const prevLastThursday = new Date(prevYear, 11, 31 - (prevDec31Day - 1) + prevDaysToLastThursday);
      const prevTotalWeeks = Math.floor((prevLastThursday.getTime() - prevFirstThursday.getTime()) / msPerDay / 7) + 1;
      
      return prevWeekNum > prevTotalWeeks ? prevTotalWeeks : (prevWeekNum > 0 ? prevWeekNum : 1);
    }
    
    return weekNum;
  };

  // Pobierz informacje o zmianie dla danego dnia na podstawie rotacji
  const getShiftForDay = (day, employeeRotationId = null) => {
    // Jeśli podano employeeRotationId, użyj go (dla trybu "Wszystkie zmiany")
    const rotationIdToUse = employeeRotationId || selectedRotation;
    
    if (!rotationIdToUse || rotationIdToUse === 'all' || !rotations.length) return null;
    
    const rotation = rotations.find(r => String(r.id) === String(rotationIdToUse));
    if (!rotation || !rotation.pattern || !rotation.daysPerCycle) return null;

    const date = new Date(currentYear, currentMonth, day);
    const dayOfWeek = date.getDay() === 0 ? 6 : date.getDay() - 1; // 0 = Poniedziałek, 6 = Niedziela
    
    // Oblicz numer tygodnia w roku
    const weekOfYear = getWeekOfYear(date);
    
    // Oblicz który tydzień w cyklu rotacji (modulo daysPerCycle)
    // Używamy modulo, aby cykl się powtarzał
    // Uwaga: weekOfYear zaczyna się od 1, więc odejmujemy 1 przed modulo
    // Upewnij się, że weekInCycle jest w zakresie 1-daysPerCycle
    let weekInCycle = ((weekOfYear - 1) % rotation.daysPerCycle) + 1;
    
    // Zabezpieczenie: jeśli modulo dało 0, ustaw na daysPerCycle
    if (weekInCycle === 0) {
      weekInCycle = rotation.daysPerCycle;
    }
    
    // Debug: odkomentuj, aby zobaczyć obliczenia
    // console.log(`Day ${day}, weekOfYear: ${weekOfYear}, daysPerCycle: ${rotation.daysPerCycle}, weekInCycle: ${weekInCycle}`);
    
    // Tymczasowe logowanie dla debugowania (usuń po znalezieniu problemu)
    if (process.env.NODE_ENV === 'development') {
      // Loguj tylko dla pierwszych kilku dni miesiąca, aby nie zaśmiecać konsoli
      if (day <= 5) {
        console.log(`[ROTATION DEBUG] Day ${day} (${new Date(currentYear, currentMonth, day).toLocaleDateString('pl-PL')}): weekOfYear=${weekOfYear}, daysPerCycle=${rotation.daysPerCycle}, weekInCycle=${weekInCycle}, pattern days:`, rotation.pattern.map(w => w.day));
      }
    }
    
    // Znajdź wzorzec dla tego tygodnia w cyklu
    // Pattern zawiera obiekty z `day` (numer tygodnia w cyklu: 1, 2, 3, ..., daysPerCycle)
    // weekInCycle jest już obliczony z modulo, więc powinien być w zakresie 1-daysPerCycle
    const weekPattern = rotation.pattern.find(w => {
      // Konwertuj oba na liczby dla porównania
      const patternDay = parseInt(w.day, 10);
      const cycleDay = parseInt(weekInCycle, 10);
      
      // Sprawdź dokładne dopasowanie (oba jako liczby)
      if (patternDay === cycleDay) {
        return true;
      }
      
      // Sprawdź też jako stringi (na wypadek problemów z typami)
      if (String(patternDay) === String(cycleDay)) {
        return true;
      }
      
      return false;
    });
    
    if (!weekPattern || !weekPattern.shifts) {
      // Debug: sprawdź dlaczego nie znaleziono wzorca (tylko w trybie deweloperskim)
      if (process.env.NODE_ENV === 'development') {
        console.log(`[DEBUG] No pattern found for day ${day} (${new Date(currentYear, currentMonth, day).toLocaleDateString('pl-PL')}): weekOfYear=${weekOfYear}, weekInCycle=${weekInCycle}, daysPerCycle=${rotation.daysPerCycle}`);
        console.log(`[DEBUG] Available patterns:`, rotation.pattern.map(w => ({ day: w.day, dayType: typeof w.day })));
        console.log(`[DEBUG] Rotation pattern:`, rotation.pattern);
      }
      return null;
    }
    
    // Debug: pokaż znaleziony wzorzec
    // console.log(`[DEBUG] Found pattern for day ${day}: weekInCycle=${weekInCycle}, pattern day=${weekPattern.day}, shifts=`, weekPattern.shifts);
    
    // Pobierz zmianę dla tego dnia tygodnia
    const shiftId = weekPattern.shifts[dayOfWeek];
    if (!shiftId || shiftId === 'off') return { isOff: true };
    
    // Pobierz informacje o zmianie z localStorage
    const shiftsKey = `workShifts_${selectedProject}`;
    const savedShifts = localStorage.getItem(shiftsKey);
    if (!savedShifts) {
      // console.log(`No shifts found for project: ${selectedProject}`);
      return null;
    }
    
    const shifts = JSON.parse(savedShifts);
    const shift = shifts.find(s => String(s.id) === String(shiftId));
    
    if (!shift) {
      // console.log(`Shift not found for shiftId: ${shiftId}, available shifts:`, shifts.map(s => s.id));
      return null;
    }
    
    return {
      isOff: false,
      shiftId: shiftId,
      shiftName: shift.name || '',
      shiftColor: shift.color || '#667eea'
    };
  };

  // Pobierz kolor zmiany dla danego dnia i typu zmiany na podstawie rotacji
  const getShiftColorForCell = (day, shiftType, employeeRotationId = null) => {
    const shiftInfo = getShiftForDay(day, employeeRotationId);
    if (!shiftInfo || shiftInfo.isOff) return null;
    
    // shiftInfo już zawiera shiftId i shiftColor, więc nie musimy ponownie pobierać z localStorage
    // Ale musimy sprawdzić, czy typ zmiany pasuje do wiersza
    
    // Mapuj nazwę zmiany na typ zmiany w grafiku
    const shiftName = (shiftInfo.shiftName || '').toLowerCase().trim();
    let expectedShiftType = null;
    
    // Sprawdź różne warianty nazwy zmiany dziennej
    // Użyj bardziej elastycznego dopasowania - sprawdź czy nazwa zawiera słowo "dzień" lub "dzien"
    const isDayShift = shiftName.includes('dzien') || 
                       shiftName.includes('day') || 
                       shiftName.includes('dzienna') ||
                       shiftName === 'dzień' ||
                       shiftName === 'dzienna' ||
                       shiftName === 'zmiana dzienna' ||
                       shiftName === 'zmiana dzień' ||
                       shiftName.startsWith('dzien') ||
                       shiftName.startsWith('dzień') ||
                       /^dzie[nń]/i.test(shiftName) ||
                       /\bdzie[nń]\b/i.test(shiftName);
    
    const isAfternoonShift = shiftName.includes('popołudni') || 
                             shiftName.includes('afternoon') || 
                             shiftName.includes('popołudniowa') ||
                             shiftName === 'popołudniowa' ||
                             shiftName === 'zmiana popołudniowa' ||
                             shiftName.startsWith('popołudni');
    
    const isNightShift = shiftName.includes('noc') || 
                         shiftName.includes('night') || 
                         shiftName.includes('nocna') ||
                         shiftName === 'nocna' ||
                         shiftName === 'noc' ||
                         shiftName === 'zmiana nocna' ||
                         shiftName.startsWith('noc');
    
    if (isDayShift) {
      expectedShiftType = 'shiftDay';
    } else if (isAfternoonShift) {
      expectedShiftType = 'shiftAfternoon';
    } else if (isNightShift) {
      expectedShiftType = 'shiftNight';
    }
    
    // Debug: loguj tylko w trybie deweloperskim
    if (process.env.NODE_ENV === 'development' && day <= 5) {
      if (shiftInfo && !shiftInfo.isOff && !expectedShiftType) {
        console.log(`[SHIFT DEBUG] Day ${day}, shiftName: "${shiftInfo.shiftName}", shiftType: "${shiftType}", expectedShiftType: null`);
      }
    }
    
    // Zwróć kolor tylko jeśli typ zmiany pasuje do wiersza
    if (expectedShiftType === shiftType) {
      return shiftInfo.shiftColor || '#667eea';
    }
    
    return null;
  };

  // Pobierz informację o zmianie dla danego dnia na podstawie rotacji
  const getShiftInfoForDay = (day) => {
    if (!selectedRotation || !rotations.length) return null;
    
    const rotation = rotations.find(r => String(r.id) === String(selectedRotation));
    if (!rotation || !rotation.pattern || !rotation.daysPerCycle) return null;

    const date = new Date(currentYear, currentMonth, day);
    const dayOfWeek = date.getDay() === 0 ? 6 : date.getDay() - 1; // 0 = Poniedziałek, 6 = Niedziela
    
    // Oblicz numer tygodnia w roku
    const weekOfYear = getWeekOfYear(date);
    
    // Oblicz który tydzień w cyklu rotacji (modulo daysPerCycle)
    const weekInCycle = ((weekOfYear - 1) % rotation.daysPerCycle) + 1;
    
    // Znajdź wzorzec dla tego tygodnia w cyklu
    const weekPattern = rotation.pattern.find(w => w.day === weekInCycle);
    if (!weekPattern || !weekPattern.shifts) return null;
    
    // Pobierz zmianę dla tego dnia tygodnia
    const shiftId = weekPattern.shifts[dayOfWeek];
    if (!shiftId || shiftId === 'off') return null;
    
    // Pobierz informacje o zmianie z localStorage
    const shiftsKey = `workShifts_${selectedProject}`;
    const savedShifts = localStorage.getItem(shiftsKey);
    if (!savedShifts) return null;
    
    const shifts = JSON.parse(savedShifts);
    const shift = shifts.find(s => String(s.id) === String(shiftId));
    
    if (!shift) return null;
    
    return {
      week: weekInCycle,
      shiftName: shift.name || '',
      shiftColor: shift.color || '#667eea'
    };
  };

  // Pobierz dostępne typy umów dla wybranego projektu
  const getAvailableContractTypes = () => {
    if (!selectedProject || !contracts[selectedProject]) return [];
    return contracts[selectedProject];
  };

  const showInfo = (message) => {
    setInfoMessage(message);
    setShowInfoModal(true);
  };

  // Generuj raport Excel
  const generateExcelReport = () => {
    if (!selectedContractType) {
      showInfo('Proszę wybrać rodzaj umowy');
      return;
    }

    // Filtruj pracowników na podstawie wybranego rodzaju umowy
    const filteredEmployees = employees.filter(employee => 
      employee.contract === selectedContractType
    );

    if (filteredEmployees.length === 0) {
      showInfo('Brak pracowników z wybranym rodzajem umowy');
      return;
    }

    // Przygotuj dane do raportu
    const reportData = filteredEmployees.map(employee => {
      const employeeId = employee.email || employee.id;
      const monthlyHours = calculateMonthlyHours(employeeId);
      const overtime = calculateOvertime(employeeId);
      const absenceSummary = calculateAbsenceSummary(employeeId);

      // Przygotuj szczegóły dni wolnych
      const absenceDetails = Object.entries(absenceSummary)
        .filter(([type, count]) => count > 0)
        .map(([type, count]) => {
          const absenceType = absenceTypes[type];
          return absenceType ? `${absenceType.label}: ${count}` : '';
        })
        .filter(Boolean)
        .join(', ');

      return {
        'Imię': employee.firstName || '',
        'Nazwisko': employee.lastName || '',
        'Stanowisko': employee.position || '',
        'Typ umowy': employee.contract || '',
        'Godziny łącznie': monthlyHours.total.toFixed(1),
        'Godziny dzienne': monthlyHours.day.toFixed(1),
        'Godziny nocne': monthlyHours.night.toFixed(1),
        'Nadgodziny dzienne': overtime.dailyDay.toFixed(1),
        'Nadgodziny nocne': overtime.dailyNight.toFixed(1),
        'Nadgodziny płatne 100%': overtime.overtime100.toFixed(1),
        'Dni wolne / Absencje': absenceDetails || 'Brak'
      };
    });

    // Utwórz arkusz kalkulacyjny
    const worksheet = XLSX.utils.json_to_sheet(reportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Raport');

    // Ustaw szerokość kolumn
    const columnWidths = [
      { wch: 15 }, // Imię
      { wch: 15 }, // Nazwisko
      { wch: 20 }, // Stanowisko
      { wch: 18 }, // Typ umowy
      { wch: 15 }, // Godziny łącznie
      { wch: 15 }, // Godziny dzienne
      { wch: 15 }, // Godziny nocne
      { wch: 18 }, // Nadgodziny dzienne
      { wch: 18 }, // Nadgodziny nocne
      { wch: 20 }, // Nadgodziny płatne 100%
      { wch: 40 }  // Dni wolne / Absencje
    ];
    worksheet['!cols'] = columnWidths;

    // Generuj nazwę pliku
    const monthName = monthNames[currentMonth];
    const fileName = `Raport_${monthName}_${currentYear}_${selectedContractType.replace(/\s+/g, '_')}.xlsx`;

    // Pobierz plik
    XLSX.writeFile(workbook, fileName);

    // Zamknij modal
    setShowReportModal(false);
    setSelectedContractType('');
  };

  // Generuj dni miesiąca
  const daysInMonth = getDaysInMonth(currentMonth, currentYear);

  const isLider = currentUser?.role === 'lider';
  const isAdmin = currentUser?.role === 'admin';
  const isSuperAdmin = currentUser?.role === 'owner';

  return (
    <div className="grafik-container">
      <div className="grafik-header-section">
        <div className="grafik-controls">
          <div className="control-group">
            <label htmlFor="project-select">Klient:</label>
            <select
              id="project-select"
              value={selectedProject}
              onChange={(e) => {
                setSelectedProject(e.target.value);
                setSelectedRotation('');
              }}
            >
              <option value="">Wybierz klienta</option>
              {projects.map(project => (
                <option key={project} value={project}>{project}</option>
              ))}
            </select>
          </div>

          {selectedProject && (
            <div className="control-group">
              <label htmlFor="rotation-select">Zmiana (Rotacja):</label>
              <select
                id="rotation-select"
                value={selectedRotation}
                onChange={(e) => setSelectedRotation(e.target.value)}
              >
                <option value="">Wybierz zmianę</option>
                {(isAdmin || isSuperAdmin) && (
                  <option value="all">Wszystkie zmiany</option>
                )}
                {rotations.map(rotation => (
                  <option key={rotation.id} value={rotation.id}>
                    {rotation.title} (Rota. {rotation.rotationNumber})
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="control-group month-navigation">
            <button 
              className="nav-button"
              onClick={() => handleYearChange('prev')}
              title="Poprzedni rok"
            >
              ‹‹
            </button>
            <button 
              className="nav-button"
              onClick={() => handleMonthChange('prev')}
              title="Poprzedni miesiąc"
            >
              ‹
            </button>
            <span className="month-year-display">
              {monthNames[currentMonth]} {currentYear}
            </span>
            <button 
              className="nav-button"
              onClick={() => handleMonthChange('next')}
              title="Następny miesiąc"
            >
              ›
            </button>
            <button 
              className="nav-button"
              onClick={() => handleYearChange('next')}
              title="Następny rok"
            >
              ››
            </button>
          </div>

          {selectedProject && selectedRotation && employees.length > 0 && (
            <div className="control-group">
              <button 
                className="clear-hours-button"
                onClick={() => setShowClearHoursModal(true)}
                title="Wyczyść wszystkie wprowadzone godziny"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M2 4h12M5.333 4V2.667a1.333 1.333 0 0 1 1.334-1.334h2.666a1.333 1.333 0 0 1 1.334 1.334V4m2 0v9.333a1.333 1.333 0 0 1-1.334 1.334H4.667a1.333 1.333 0 0 1-1.334-1.334V4h9.334zM6.667 7.333v4M9.333 7.333v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Wyczyść godziny
              </button>
            </div>
          )}
        </div>
      </div>

      {selectedProject && selectedRotation && employees.length > 0 ? (() => {
        const daysInMonth = getDaysInMonth(currentMonth, currentYear);
        return (
        <div className="grafik-single-card-container">
          <div className="grafik-single-card">
            <div className="grafik-card-content">
              <div className="grafik-table-wrapper-single">
                <table className="grafik-table-single">
              <thead>
                    {/* Nagłówek z kolumnami */}
                    <tr className="main-header-row-single">
                      <th className="col-no-single">No.</th>
                      <th className="col-firstname-single">Imię</th>
                      <th className="col-lastname-single">Nazwisko</th>
                      <th className="col-user-single">USER</th>
                      <th className="col-position-single">STANOWISKO</th>
                      <th className="col-contract-single">UMOWA</th>
                      <th className="col-shift-type-single">Typ zmiany</th>
                  {Array.from({ length: daysInMonth }, (_, i) => {
                    const day = i + 1;
                    const date = new Date(currentYear, currentMonth, day);
                    const dayOfWeek = date.getDay();
                        const dayName = dayNames[dayOfWeek === 0 ? 6 : dayOfWeek - 1];
                        const monthName = monthNames[currentMonth].substring(0, 3).toLowerCase();
                    return (
                          <th key={day} className="col-date-single">
                            <div className="date-day-name">{dayName}</div>
                            <div className="date-day-number">{day} {monthName}</div>
                      </th>
                    );
                  })}
                      <th colSpan="17" className="col-summary-header-single">PODSUMOWANIE</th>
                    </tr>
                    {/* Nagłówek sekcji PODSUMOWANIE */}
                    <tr className="summary-header-row-single">
                      <th className="empty-header-single"></th>
                      <th className="empty-header-single"></th>
                      <th className="empty-header-single"></th>
                      <th className="empty-header-single empty-header-static"></th>
                      <th className="empty-header-single empty-header-static"></th>
                      <th className="empty-header-single empty-header-static"></th>
                      <th className="empty-header-single empty-header-static"></th>
                      {Array.from({ length: daysInMonth }, (_, i) => {
                        const day = i + 1;
                        const date = new Date(currentYear, currentMonth, day);
                        const weekOfYear = getWeekOfYear(date);
                        return (
                          <th key={i} className="empty-header-single">
                            <div className="week-number-in-cell">Tydz. {weekOfYear}</div>
                          </th>
                        );
                      })}
                      <th className="summary-col-single">Suma wszystkich godzin</th>
                      <th className="summary-col-single">Suma godz dziennej zmiany</th>
                      <th className="summary-col-single">Suma godzin popołudniowej zmiany</th>
                      <th className="summary-col-single">Suma godzin nocnej zmiany</th>
                      <th className="summary-col-single">Nad.Dzienne</th>
                      <th className="summary-col-single">Nad.Nocne</th>
                      <th className="summary-col-single">Soboty</th>
                      <th className="summary-col-single">Niedziele</th>
                      <th className="summary-col-single">Urlop wypoczynkowy</th>
                      <th className="summary-col-single">Chorobowe</th>
                      <th className="summary-col-single">Delegacja</th>
                      <th className="summary-col-single">Urlop z tytułu opieki</th>
                      <th className="summary-col-single">Urlop okolicznościowy</th>
                      <th className="summary-col-single">Usprawiedliwiona nieobecność w pracy (nie płatne)</th>
                      <th className="summary-col-single">Urlop na żądanie</th>
                      <th className="summary-col-single">Pekka's Day</th>
                      <th className="summary-col-single">Siła wyższa</th>
                </tr>
              </thead>
              <tbody>
                    {employees.map((employee, empIndex) => {
                  const employeeId = employee.email || employee.id;
                      const stats = calculateSummaryStatistics(employeeId);
                      
                      // Definicje 3 wierszy dla każdego pracownika (tylko zmiany, bez nadgodzin)
                      const shiftRows = [
                        { type: 'shiftDay', label: 'Z.Dzienna', timeRange: '6:00-14:00', shiftType: 'shiftDay' },
                        { type: 'shiftAfternoon', label: 'Z.Popołudniowa', timeRange: '14:00-22:00', shiftType: 'shiftAfternoon' },
                        { type: 'shiftNight', label: 'Z.Nocna', timeRange: '22:00-6:00', shiftType: 'shiftNight' }
                      ];
                      
                      return shiftRows.map((row, rowIndex) => {
                        const isFirstRow = rowIndex === 0;
                        const isLastRow = rowIndex === shiftRows.length - 1;
                        const isOvertimeRow = false; // Nie ma już wierszy nadgodzin
                        const rowKey = `${employeeId}-${row.type}`;
                  
                  return (
                          <tr key={rowKey} className={`shift-row-single ${row.type} ${isFirstRow ? 'employee-first-row-single' : ''} ${isLastRow ? 'employee-last-row-single' : ''}`}>
                            {/* Kolumny informacji o pracowniku - tylko w pierwszym wierszu */}
                            {isFirstRow && (
                              <>
                                <td rowSpan="3" className="col-no-single">{empIndex + 1}</td>
                                <td rowSpan="3" className="col-firstname-single">{employee.firstName || ''}</td>
                                <td rowSpan="3" className="col-lastname-single">{employee.lastName || ''}</td>
                                <td rowSpan="3" className="col-user-single">{employee.systemUser || employee.email?.split('@')[0] || employee.id || ''}</td>
                                <td rowSpan="3" className="col-position-single">{employee.position || ''}</td>
                                <td rowSpan="3" className="col-contract-single">{employee.contract || ''}</td>
                              </>
                            )}
                            
                            {/* Etykieta wiersza */}
                            <td className="col-shift-label-single">{row.label}</td>
                            
                            {/* Komórki z datami */}
                      {Array.from({ length: daysInMonth }, (_, i) => {
                        const day = i + 1;
                        const dateKey = formatDateKey(day);
                        const dayData = scheduleData[dateKey];
                              let employeeDayData = dayData && dayData[employeeId] ? dayData[employeeId] : getDefaultShiftData();
                        
                        // Kompatybilność wsteczna: konwersja starego formatu
                              if (employeeDayData.hours !== undefined && employeeDayData.shiftDay === undefined) {
                                // Stary format - konwertuj na nowy
                                const oldAbsenceType = employeeDayData.absenceType || '';
                          employeeDayData = {
                                  ...getDefaultShiftData(),
                                  shiftDay: employeeDayData.hours || 0,
                                  shiftNight: employeeDayData.nightHours || 0,
                                  // Jeśli była absencja w starym formacie, przypisz ją do zmiany dziennej (domyślnie)
                                  absenceTypeDay: oldAbsenceType
                                };
                              }
                              // Kompatybilność wsteczna: jeśli jest stary format z absenceType, skonwertuj
                              if (employeeDayData.absenceType && !employeeDayData.absenceTypeDay && !employeeDayData.absenceTypeAfternoon && !employeeDayData.absenceTypeNight) {
                                employeeDayData.absenceTypeDay = employeeDayData.absenceType;
                                delete employeeDayData.absenceType;
                              }
                        
                        const date = new Date(currentYear, currentMonth, day);
                        const dayOfWeek = date.getDay();
                        const isWeekendDay = dayOfWeek === 0 || dayOfWeek === 6;
                        const isHolidayDay = isHoliday(day);
                              
                              // Sprawdź czy dzień jest wyłączony w rotacji
                              // W trybie "Wszystkie zmiany" użyj rotacji pracownika
                              const employeeRotationId = selectedRotation === 'all' ? (employee.rotationId ? String(employee.rotationId) : null) : null;
                              const shiftInfo = getShiftForDay(day, employeeRotationId);
                              const isDayOff = shiftInfo && shiftInfo.isOff;
                              
                              // Pobierz kolor zmiany dla tej komórki
                              // Ważne: wywołaj funkcję dla każdego dnia i każdego typu zmiany
                              const shiftColor = getShiftColorForCell(day, row.shiftType, employeeRotationId);
                              
                              // Debug: sprawdź czy shiftColor jest ustawiony
                              // console.log(`Day ${day}, shiftType ${row.shiftType}, shiftColor:`, shiftColor, 'shiftInfo:', shiftInfo);
                              
                              // Pobierz wartość dla tego typu zmiany
                              const value = employeeDayData[row.shiftType] || 0;
                              
                              // Sprawdź rodzaj zatrudnienia pracownika
                              const employmentType = employee?.employmentType || '';
                              const isContractWork = employmentType === 'Umowa zlecenie';
                              
                              // Sprawdź czy są nadgodziny (wartość > 8 dla typów zmian)
                              // Dla "Umowa zlecenie" nie pokazujemy nadgodzin (wszystkie godziny jako zwykłe)
                              const hasOvertime = !isContractWork && value > 8 && (row.shiftType === 'shiftDay' || row.shiftType === 'shiftAfternoon' || row.shiftType === 'shiftNight');
                              
                              // Sprawdź explicit nadgodziny (zapisanę osobno)
                              const explicitOvertimeDay = employeeDayData.overtimeDay || 0;
                              const explicitOvertimeNight = employeeDayData.overtimeNight || 0;
                              
                              // Określ typ nadgodzin dla wyświetlenia litery D lub N
                              // Priorytet: najpierw sprawdź explicit nadgodziny
                              let overtimeTypeForDisplay = null;
                              if (hasOvertime) {
                                // Jeśli są explicit nadgodziny nocne, wyświetl 'N'
                                if (explicitOvertimeNight > 0) {
                                  overtimeTypeForDisplay = 'N'; // Nad.Nocne
                                }
                                // Jeśli są explicit nadgodziny dzienne, wyświetl 'D'
                                else if (explicitOvertimeDay > 0) {
                                  overtimeTypeForDisplay = 'D'; // Nad.Dzienne
                                }
                                // Jeśli nie ma explicit nadgodzin, określ na podstawie typu zmiany
                                else if (row.shiftType === 'shiftNight') {
                                  overtimeTypeForDisplay = 'N'; // Nad.Nocne
                                } else if (row.shiftType === 'shiftDay' || row.shiftType === 'shiftAfternoon') {
                                  overtimeTypeForDisplay = 'D'; // Nad.Dzienne
                                }
                              }
                              
                              // Pobierz absencję dla konkretnego typu zmiany
                              let absenceTypeForShift = '';
                              if (row.shiftType === 'shiftDay') {
                                absenceTypeForShift = employeeDayData.absenceTypeDay || '';
                              } else if (row.shiftType === 'shiftAfternoon') {
                                absenceTypeForShift = employeeDayData.absenceTypeAfternoon || '';
                              } else if (row.shiftType === 'shiftNight') {
                                absenceTypeForShift = employeeDayData.absenceTypeNight || '';
                              }
                              
                              const hasAbsence = !!absenceTypeForShift;
                              const absenceLabel = hasAbsence ? absenceTypeLabels[absenceTypeForShift] || absenceTypeForShift : '';
                              
                              // Style dla komórki - kolor tła jeśli jest przypisana zmiana w rotacji lub nadgodziny
                              // Jeśli są nadgodziny, użyj różowego tła
                              let backgroundColor = '';
                              if (hasOvertime) {
                                backgroundColor = 'rgba(251, 113, 133, 0.2)'; // Różowe tło dla nadgodzin
                              } else if (hasAbsence) {
                                backgroundColor = 'rgba(224, 242, 254, 0.7)'; // Tło absencji
                              } else if (shiftColor) {
                                backgroundColor = `${shiftColor}20`; // Kolor rotacji
                              }
                              
                              const cellStyle = shiftColor || hasOvertime ? {
                                backgroundColor: backgroundColor,
                                borderLeft: shiftColor ? `3px solid ${shiftColor}` : undefined,
                                borderRight: shiftColor ? `3px solid ${shiftColor}` : undefined,
                                position: 'relative',
                                zIndex: 1
                              } : {};
                        
                        return (
                          <td 
                            key={day} 
                                  className={`day-cell-single ${isWeekendDay ? 'weekend' : ''} ${isHolidayDay ? 'holiday' : ''} ${hasAbsence ? 'has-absence' : ''} ${isDayOff ? 'day-off' : ''} ${shiftColor ? 'has-shift-color' : ''} ${hasOvertime ? 'has-overtime' : ''}`}
                                  style={cellStyle}
                                >
                                  {isDayOff && !hasAbsence && value === 0 ? (
                                    <div className="day-off-label" title="Dzień wyłączony w rotacji (możesz wpisać godziny)">-</div>
                                  ) : hasAbsence && rowIndex === 0 && !isOvertimeRow ? (
                                    <div 
                                      className="absence-label-single"
                                      title="Kliknij, aby zmienić typ absencji lub dodać nadgodziny"
                                      onClick={() => {
                                        setAbsenceModalData({ 
                                          employeeId, 
                                          day,
                                          shiftType: row.shiftType, // Dodaj typ zmiany
                                          currentType: absenceTypeForShift,
                                          currentOvertimeDay: employeeDayData.overtimeDay || 0,
                                          currentOvertimeNight: employeeDayData.overtimeNight || 0
                                        });
                                        setModalMode(null); // Resetuj tryb, aby pokazać wybór
                                        setShowAbsenceModal(true);
                                      }}
                                      style={{ cursor: 'pointer' }}
                                    >
                                      {absenceLabel}
                                </div>
                                  ) : hasAbsence && !isOvertimeRow ? (
                                    <div className="absence-label-single">{absenceLabel}</div>
                              ) : (
                                    <div className={isOvertimeRow ? "input-without-absence-button" : "input-with-absence-button"}>
                                  <input
                                    type="number"
                                    min="0"
                                    max="24"
                                    step="0.5"
                                        value={value > 0 ? value : ''}
                                        onChange={(e) => handleShiftChange(employeeId, day, row.shiftType, e.target.value)}
                                        placeholder=""
                                        className="shift-input-single"
                                        title={shiftColor ? `${row.label} (${row.timeRange}) - Zmiana przypisana w rotacji` : `${row.label} (${row.timeRange})`}
                                      />
                                      {!isOvertimeRow && (
                                        hasOvertime ? (
                <button 
                                            type="button"
                                            className="overtime-indicator-button"
                                            onClick={() => {
                                              // Określ typ nadgodzin
                                              let overtimeType = 'overtimeDay';
                                              if (row.shiftType === 'shiftNight') {
                                                overtimeType = 'overtimeNight';
                                              } else if (row.shiftType === 'shiftDay' || row.shiftType === 'shiftAfternoon') {
                                                overtimeType = 'overtimeDay';
                                              }
                                              
                                              // Pobierz aktualną absencję dla tego typu zmiany
                                              let currentAbsenceType = '';
                                              if (row.shiftType === 'shiftDay') {
                                                currentAbsenceType = employeeDayData.absenceTypeDay || '';
                                              } else if (row.shiftType === 'shiftAfternoon') {
                                                currentAbsenceType = employeeDayData.absenceTypeAfternoon || '';
                                              } else if (row.shiftType === 'shiftNight') {
                                                currentAbsenceType = employeeDayData.absenceTypeNight || '';
                                              }
                                              
                                              // Oblicz nadgodziny
                                              const overtimeHours = value > 8 ? value - 8 : (employeeDayData[overtimeType] || 0);
                                              
                                              setAbsenceModalData({ 
                                                employeeId, 
                                                day,
                                                shiftType: row.shiftType,
                                                overtimeType: overtimeType,
                                                overtimeHours: overtimeHours.toString(),
                                                currentType: currentAbsenceType,
                                                currentOvertimeDay: employeeDayData.overtimeDay || 0,
                                                currentOvertimeNight: employeeDayData.overtimeNight || 0
                                              });
                                              setModalMode('overtime');
                                              setShowAbsenceModal(true);
                                            }}
                                            title={`Nadgodziny ${overtimeTypeForDisplay === 'D' ? 'Dzienne' : 'Nocne'} - Kliknij, aby edytować`}
                                          >
                                            {overtimeTypeForDisplay}
                </button>
                                        ) : (
              <button 
                                            type="button"
                                            className="absence-button"
                                            onClick={() => {
                                              // Pobierz aktualną absencję dla tego typu zmiany
                                              let currentAbsenceType = '';
                                              if (row.shiftType === 'shiftDay') {
                                                currentAbsenceType = employeeDayData.absenceTypeDay || '';
                                              } else if (row.shiftType === 'shiftAfternoon') {
                                                currentAbsenceType = employeeDayData.absenceTypeAfternoon || '';
                                              } else if (row.shiftType === 'shiftNight') {
                                                currentAbsenceType = employeeDayData.absenceTypeNight || '';
                                              }
                                              
                                              setAbsenceModalData({ 
                                                employeeId, 
                                                day,
                                                shiftType: row.shiftType, // Dodaj typ zmiany
                                                currentType: currentAbsenceType,
                                                currentOvertimeDay: employeeDayData.overtimeDay || 0,
                                                currentOvertimeNight: employeeDayData.overtimeNight || 0
                                              });
                                              setModalMode(null); // Resetuj tryb, aby pokazać wybór
                                              setShowAbsenceModal(true);
                                            }}
                                            title="Dodaj nadgodziny lub absencję"
                                          >
                                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                              <path d="M7 1V13M1 7H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
                                        )
                                      )}
            </div>
                                  )}
                                </td>
                          );
                        })}
                            
                            {/* Komórki sekcji PODSUMOWANIE - tylko w pierwszym wierszu */}
                            {isFirstRow && (
                              <>
                                <td className="summary-cell-single">{stats.totalHours.toFixed(1)}</td>
                                <td className="summary-cell-single">{stats.totalDayShift.toFixed(1)}</td>
                                <td className="summary-cell-single">{stats.totalAfternoonShift.toFixed(1)}</td>
                                <td className="summary-cell-single">{stats.totalNightShift.toFixed(1)}</td>
                                <td className="summary-cell-single">{stats.overtimeDay.toFixed(1)}</td>
                                <td className="summary-cell-single">{stats.overtimeNight.toFixed(1)}</td>
                                <td className="summary-cell-single">{stats.saturdays.toFixed(1)}</td>
                                <td className="summary-cell-single">{stats.sundays.toFixed(1)}</td>
                                <td className="summary-cell-single">{stats.vacation}</td>
                                <td className="summary-cell-single">{stats.sickLeave}</td>
                                <td className="summary-cell-single">{stats.delegation}</td>
                                <td className="summary-cell-single">{stats.careLeave}</td>
                                <td className="summary-cell-single">{stats.occasionalLeave}</td>
                                <td className="summary-cell-single">{stats.unpaidAbsence}</td>
                                <td className="summary-cell-single">{stats.leaveOnDemand}</td>
                                <td className="summary-cell-single">{stats.pekkasDay}</td>
                                <td className="summary-cell-single">{stats.forceMajeure}</td>
                              </>
                            )}
                          </tr>
                        );
                      });
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
        );
      })() : selectedProject && selectedRotation && employees.length === 0 ? (
        <div className="no-employees-message">
          <p>Brak pracowników przypisanych do wybranej zmiany w tym projekcie.</p>
        </div>
      ) : !selectedProject ? (
        <div className="no-project-message">
          <p>Wybierz projekt, aby wyświetlić grafik</p>
        </div>
      ) : !selectedRotation ? (
        <div className="no-rotation-message">
          <p>Wybierz zmianę (rotację), aby wyświetlić grafik pracowników</p>
        </div>
      ) : null}

      {/* Modal do wyboru rodzaju umowy */}
      {showReportModal && (
        <div className="report-modal-overlay" onClick={() => {
          setShowReportModal(false);
          setSelectedContractType('');
        }}>
          <div className="report-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="report-modal-header">
              <h3>Generuj raport Excel</h3>
              <button 
                className="report-modal-close"
                onClick={() => {
                  setShowReportModal(false);
                  setSelectedContractType('');
                }}
                aria-label="Zamknij"
              >
                ×
              </button>
            </div>
            <div className="report-modal-body">
              <div className="report-modal-info">
                <p>Wybierz rodzaj umowy, aby wygenerować raport dla pracowników z tym typem umowy.</p>
                <p className="report-modal-month">
                  <strong>Miesiąc:</strong> {monthNames[currentMonth]} {currentYear}
                </p>
                <p className="report-modal-project">
                  <strong>Klient:</strong> {selectedProject}
                </p>
              </div>
              <div className="report-modal-form">
                <label htmlFor="contract-type-select">
                  Rodzaj umowy: <span className="required-asterisk">*</span>
                </label>
                <select
                  id="contract-type-select"
                  value={selectedContractType}
                  onChange={(e) => setSelectedContractType(e.target.value)}
                  className="contract-type-select"
                >
                  <option value="">Wybierz rodzaj umowy</option>
                  {getAvailableContractTypes().map((contractType, index) => (
                    <option key={index} value={contractType}>
                      {contractType}
                    </option>
                  ))}
                </select>
                {selectedContractType && (
                  <div className="report-modal-preview">
                    <p>
                      Raport zostanie wygenerowany dla <strong>
                        {employees.filter(e => e.contract === selectedContractType).length}
                      </strong> pracownik{employees.filter(e => e.contract === selectedContractType).length === 1 ? 'a' : 'ów'} z umową: <strong>{selectedContractType}</strong>
                    </p>
                  </div>
                )}
              </div>
            </div>
            <div className="report-modal-footer">
              <button 
                className="report-modal-cancel"
                onClick={() => {
                  setShowReportModal(false);
                  setSelectedContractType('');
                }}
              >
                Anuluj
              </button>
              <button 
                className="report-modal-generate"
                onClick={generateExcelReport}
                disabled={!selectedContractType}
              >
                Generuj raport
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal informacyjny */}
      {showInfoModal && (
        <div className="modal-overlay" onClick={() => setShowInfoModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Informacja</h3>
              <button className="modal-close" onClick={() => setShowInfoModal(false)}>×</button>
            </div>
            <div style={{ padding: '32px' }}>
              <p>{infoMessage}</p>
              <div className="modal-actions">
                <button className="btn-confirm" onClick={() => setShowInfoModal(false)}>
                  OK
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal potwierdzenia wyczyszczenia godzin */}
      {showClearHoursModal && (
        <div className="modal-overlay" onClick={() => setShowClearHoursModal(false)}>
          <div className="modal-content delete-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Wyczyść wszystkie godziny</h3>
              <button className="modal-close" onClick={() => setShowClearHoursModal(false)}>×</button>
            </div>
            <div style={{ padding: '32px' }}>
              <div className="delete-warning">
                <p>Czy na pewno chcesz wyczyścić wszystkie wprowadzone godziny dla wszystkich pracowników w tym miesiącu?</p>
                <p style={{ marginTop: '12px', fontSize: '14px', color: '#6b7280' }}>
                  <strong>Uwaga:</strong> Ta operacja wyczyści wszystkie godziny (zmiany dzienne, popołudniowe, nocne oraz nadgodziny), 
                  ale zachowa wprowadzone typy absencji.
                </p>
              </div>
              <div className="modal-actions">
                <button 
                  className="btn-cancel" 
                  onClick={() => setShowClearHoursModal(false)}
                >
                  Anuluj
                </button>
                <button 
                  className="btn-delete" 
                  onClick={handleClearAllHours}
                >
                  Wyczyść wszystkie godziny
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal do zmiany typu absencji */}
      {showAbsenceModal && absenceModalData && (
        <div className="modal-overlay" onClick={() => {
          setShowAbsenceModal(false);
          setAbsenceModalData(null);
          setModalMode(null);
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{modalMode === 'overtime' ? 'Dodaj nadgodziny' : modalMode === 'absence' ? 'Zmień typ absencji' : 'Wybierz opcję'}</h3>
              <button className="modal-close" onClick={() => {
                setShowAbsenceModal(false);
                setAbsenceModalData(null);
                setModalMode(null);
              }}>×</button>
            </div>
            <div style={{ padding: '32px' }}>
              {!modalMode ? (
                // Wybór trybu: Nadgodziny lub Absencje
                <div>
                  <div style={{ marginBottom: '24px' }}>
                    <p style={{ marginBottom: '16px', color: '#6b7280', fontSize: '14px' }}>
                      Wybierz, co chcesz dodać lub zmienić:
                    </p>
                    <div style={{ display: 'flex', gap: '16px', flexDirection: 'column' }}>
                      <button
                        onClick={() => setModalMode('overtime')}
                        style={{
                          width: '100%',
                          padding: '16px',
                          border: '2px solid #3b82f6',
                          borderRadius: '8px',
                          backgroundColor: '#eff6ff',
                          color: '#1e40af',
                          fontSize: '16px',
                          fontWeight: 600,
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = '#dbeafe';
                          e.target.style.transform = 'translateY(-2px)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = '#eff6ff';
                          e.target.style.transform = 'translateY(0)';
                        }}
                      >
                        ⏰ Nadgodziny
                      </button>
                      <button
                        onClick={() => setModalMode('absence')}
                        style={{
                          width: '100%',
                          padding: '16px',
                          border: '2px solid #10b981',
                          borderRadius: '8px',
                          backgroundColor: '#ecfdf5',
                          color: '#065f46',
                          fontSize: '16px',
                          fontWeight: 600,
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = '#d1fae5';
                          e.target.style.transform = 'translateY(-2px)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = '#ecfdf5';
                          e.target.style.transform = 'translateY(0)';
                        }}
                      >
                        📅 Absencje
                      </button>
                    </div>
                  </div>
                  <div className="modal-actions">
                    <button 
                      className="btn-cancel" 
                      onClick={() => {
                        setShowAbsenceModal(false);
                        setAbsenceModalData(null);
                        setModalMode(null);
                      }}
                    >
                      Anuluj
                    </button>
                  </div>
                </div>
              ) : modalMode === 'overtime' ? (
                // Formularz nadgodzin
                <div>
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                      Wybierz typ nadgodzin:
                    </label>
                    <select
                      value={absenceModalData.overtimeType || 'overtimeDay'}
                      onChange={(e) => setAbsenceModalData({ ...absenceModalData, overtimeType: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '1.5px solid #d1d5db',
                        borderRadius: '8px',
                        fontSize: '15px',
                        cursor: 'pointer',
                        marginBottom: '16px'
                      }}
                    >
                      <option value="overtimeDay">Nad.Dzienne (6:00-14:00)</option>
                      <option value="overtimeNight">Nad.Nocne (22:00-6:00)</option>
                    </select>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                      Liczba godzin:
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="24"
                      step="0.5"
                      value={absenceModalData.overtimeHours || (absenceModalData.overtimeType === 'overtimeDay' ? absenceModalData.currentOvertimeDay : absenceModalData.currentOvertimeNight) || ''}
                      onChange={(e) => setAbsenceModalData({ ...absenceModalData, overtimeHours: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '1.5px solid #d1d5db',
                        borderRadius: '8px',
                        fontSize: '15px'
                      }}
                      placeholder="0"
                    />
                  </div>
                  <div className="modal-actions" style={{ display: 'flex', gap: '12px', justifyContent: 'space-between' }}>
                    <button 
                      className="btn-cancel" 
                      onClick={() => {
                        setModalMode(null);
                      }}
                    >
                      Wstecz
                    </button>
                    <div style={{ display: 'flex', gap: '12px' }}>
                      <button 
                        className="btn-delete" 
                        onClick={() => {
                          const overtimeType = absenceModalData.overtimeType || 'overtimeDay';
                          handleClearOvertime(absenceModalData.employeeId, absenceModalData.day, overtimeType);
                        }}
                        style={{
                          padding: '12px 24px',
                          backgroundColor: '#ef4444',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          fontSize: '15px',
                          fontWeight: 600,
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = '#dc2626';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = '#ef4444';
                        }}
                      >
                        Wyczyść
                      </button>
                      <button 
                        className="btn-confirm" 
                        onClick={() => {
                          const overtimeHours = parseFloat(absenceModalData.overtimeHours) || 0;
                          const overtimeType = absenceModalData.overtimeType || 'overtimeDay';
                          const shiftType = absenceModalData.shiftType; // Typ zmiany (shiftDay, shiftAfternoon, shiftNight)
                          
                          const dateKey = formatDateKey(absenceModalData.day);
                          const newData = { ...scheduleData };
                          if (!newData[dateKey]) {
                            newData[dateKey] = {};
                          }
                          if (!newData[dateKey][absenceModalData.employeeId]) {
                            newData[dateKey][absenceModalData.employeeId] = getDefaultShiftData();
                          }
                          
                          // Wyczyść nadgodziny przeciwnego typu (jeśli użytkownik zmienia typ)
                          if (overtimeType === 'overtimeDay') {
                            newData[dateKey][absenceModalData.employeeId].overtimeNight = 0;
                          } else if (overtimeType === 'overtimeNight') {
                            newData[dateKey][absenceModalData.employeeId].overtimeDay = 0;
                          }
                          
                          // Zapisz nadgodziny
                          newData[dateKey][absenceModalData.employeeId][overtimeType] = overtimeHours;
                          
                          // Jeśli jest shiftType, zaktualizuj liczbę godzin w komórce tylko dla nadgodzin dziennych
                          // Dla nadgodzin nocnych nie modyfikujemy shiftDay/shiftAfternoon (są niezależne)
                          if (shiftType && overtimeType === 'overtimeDay') {
                            // Nadgodziny dzienne - zaktualizuj shiftDay lub shiftAfternoon
                            const totalHours = 8 + overtimeHours;
                            if (shiftType === 'shiftDay' || shiftType === 'shiftAfternoon') {
                              newData[dateKey][absenceModalData.employeeId][shiftType] = totalHours;
                            }
                          } else if (shiftType && overtimeType === 'overtimeNight') {
                            // Nadgodziny nocne - zaktualizuj tylko shiftNight, jeśli to jest zmiana nocna
                            if (shiftType === 'shiftNight') {
                              const totalHours = 8 + overtimeHours;
                              newData[dateKey][absenceModalData.employeeId][shiftType] = totalHours;
                            }
                            // Jeśli nadgodziny nocne są przypisane do zmiany dziennej/popołudniowej, nie modyfikujemy shiftDay/shiftAfternoon
                            // (nadgodziny nocne są niezależne od zmian dziennych)
                            // Ale musimy upewnić się, że shiftDay nie zawiera nadgodzin dziennych
                            // Jeśli shiftDay > 8 i nie ma explicit nadgodzin dziennych, ustaw na 8
                            const currentShiftDay = newData[dateKey][absenceModalData.employeeId].shiftDay || 0;
                            if (currentShiftDay > 8 && (shiftType === 'shiftDay' || shiftType === 'shiftAfternoon')) {
                              newData[dateKey][absenceModalData.employeeId].shiftDay = 8;
                            }
                          }
                          
                          saveScheduleData(newData);
                          setShowAbsenceModal(false);
                          setAbsenceModalData(null);
                          setModalMode(null);
                        }}
                      >
                        Zapisz
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                // Formularz absencji
                <div>
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                      Wybierz typ absencji:
                    </label>
                    <select
                      value={absenceModalData.newType !== undefined ? absenceModalData.newType : (absenceModalData.currentType || '')}
                      onChange={(e) => setAbsenceModalData({ ...absenceModalData, newType: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '1.5px solid #d1d5db',
                        borderRadius: '8px',
                        fontSize: '15px',
                        cursor: 'pointer'
                      }}
                    >
                      <option value="">Brak absencji</option>
                      {Object.entries(absenceTypes).map(([key, type]) => (
                        <option key={key} value={key}>
                          {type.icon} {type.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="modal-actions">
                    <button 
                      className="btn-cancel" 
                      onClick={() => {
                        setModalMode(null);
                      }}
                    >
                      Wstecz
                    </button>
                    <button 
                      className="btn-confirm" 
                      onClick={() => {
                        if (absenceModalData.newType !== undefined && absenceModalData.shiftType) {
                          handleAbsenceChange(absenceModalData.employeeId, absenceModalData.day, absenceModalData.shiftType, absenceModalData.newType);
                        }
                        setShowAbsenceModal(false);
                        setAbsenceModalData(null);
                        setModalMode(null);
                      }}
                    >
                      Zapisz
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Grafik;
